import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Logger } from "../../infra/logger/logger";
import equipmentRepository from "../../infra/repositories/equipment-repository";
import { Equipment } from "../../domain/entities/equipment";
import { EquipmentInputFilter } from "../../domain/models/equipment-input-filter";
import { EquipmentAttributeDefinition } from "../../domain/entities/equipment-attribute-definition";

export const useEquipmentsByFilter = (
  filter: EquipmentInputFilter,
  enabled: boolean = true,
) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["equipments", filter],
    queryFn: async () => {
      try {
        const result = await equipmentRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] ||
              "Error fetching equipments",
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: enabled,
  });
};

export const useCreateEquipmentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Equipment) => {
      const result = await equipmentRepository.create(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error creating equipment",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useUpdateEquipmentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Equipment) => {
      const result = await equipmentRepository.update(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error updating equipment",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useDeleteEquipmentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await equipmentRepository.delete(id);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error deleting equipment",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useUpdateMyEquipmentAttributesMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      attributes,
    }: {
      id: string;
      attributes: string;
    }) => {
      const result = await equipmentRepository.updateMyAttributes(
        id,
        attributes,
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error updating attributes",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useLogVehicleMileageMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      month,
      year,
      kms,
    }: {
      id: string;
      month: number;
      year: number;
      kms: number;
    }) => {
      const result = await equipmentRepository.logMileage(id, month, year, kms);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error logging mileage",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useEquipmentAttributeDefinitions = (
  filter: { equipmentTypeId?: string },
  enabled: boolean = true,
) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["equipmentAttributeDefinitions", filter],
    queryFn: async () => {
      try {
        const result =
          await equipmentRepository.getAttributeDefinitions(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] ||
              "Error fetching attribute definitions",
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: enabled,
  });
};

export const useCreateEquipmentAttributeDefinitionMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EquipmentAttributeDefinition) => {
      const result = await equipmentRepository.createAttributeDefinition(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["equipmentAttributeDefinitions"],
      });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error creating attribute definition",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useUpdateEquipmentAttributeDefinitionMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EquipmentAttributeDefinition) => {
      const result = await equipmentRepository.updateAttributeDefinition(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["equipmentAttributeDefinitions"],
      });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error updating attribute definition",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useVehicleMileageLogs = (
  equipmentId?: string,
  enabled: boolean = true,
) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["vehicleMileageLogs", equipmentId],
    queryFn: async () => {
      try {
        if (!equipmentId) throw new Error("Equipment ID is required");
        const result = await equipmentRepository.getMileageLogs(equipmentId);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] ||
              "Error fetching mileage logs",
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: enabled && !!equipmentId,
  });
};

export const useAllVehicleMileageLogs = (
  filter?: { month?: number; year?: number },
  enabled: boolean = true,
) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["allVehicleMileageLogs", filter],
    queryFn: async () => {
      try {
        const result = await equipmentRepository.getAllMileageLogs(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] ||
              "Error fetching all mileage logs",
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: enabled,
  });
};

export const useEquipmentMovements = (
  equipmentId?: string,
  enabled: boolean = true,
) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["equipmentMovements", equipmentId],
    queryFn: async () => {
      try {
        if (!equipmentId) throw new Error("Equipment ID is required");
        const result = await equipmentRepository.getMovements(equipmentId);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] ||
              "Error fetching movements",
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: enabled && !!equipmentId,
  });
};

export const useCreateEquipmentMovementMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const result = await equipmentRepository.createMovement(id, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipmentMovements"] });
      queryClient.invalidateQueries({ queryKey: ["equipments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error creating movement",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useUpdateEquipmentMovementMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      movementId,
      data,
    }: {
      id: string;
      movementId: string;
      data: any;
    }) => {
      const result = await equipmentRepository.updateMovement(
        id,
        movementId,
        data,
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipmentMovements"] });
      queryClient.invalidateQueries({ queryKey: ["equipments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error updating movement",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useEquipmentDocuments = (
  equipmentId?: string,
  enabled: boolean = true,
) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["equipmentDocuments", equipmentId],
    queryFn: async () => {
      try {
        if (!equipmentId) throw new Error("Equipment ID is required");
        const result = await equipmentRepository.getDocuments(equipmentId);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata?.errors?.[0] ||
              "Error fetching documents",
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata?.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
    enabled: enabled && !!equipmentId,
  });
};

export const useCreateEquipmentDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const result = await equipmentRepository.createDocument(id, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipmentDocuments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error creating document",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};

export const useDeleteEquipmentDocumentMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (docId: string) => {
      const result = await equipmentRepository.deleteDocument(docId);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipmentDocuments"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors || [
          "Error deleting document",
        ];
        logger.error(errors[0], error.response?.data?.metadata);
        throw new Error(errors);
      }
      throw error;
    },
  });
};
